import { html, unsafeCSS } from 'lit';

import { property, state } from 'lit/decorators.js';
import { DERElement } from '~/common/der-element';
import Button from '../../button';
import Link from '../../link';
import { styles } from './progress-bar-item.styles';
import { ProgressBarButtonConfig, ProgressBarLinkConfig } from './types';

export interface UiLinkConfig {
    type: 'link';
    config: Partial<Link>;
}

export interface UiButtonConfig {
    type: 'button';
    config: Partial<Button>;
}

export class ProgressBarItem extends DERElement {
    @property({ type: Object }) backButton?: ProgressBarLinkConfig | ProgressBarButtonConfig;
    @property({ type: Object }) nextButton?: ProgressBarLinkConfig | ProgressBarButtonConfig;
    @property({ type: String }) label = '';

    @state()
    private _isCurrentItem = false;

    static override styles = unsafeCSS(styles);

    private _changeEventNames: string[] = ['der.validity-change'];

    override connectedCallback(): void {
        super.connectedCallback();
        this.setValidityValue();

        this._changeEventNames.forEach((name) => this.addEventListener(name, this.setValidityValue));
    }

    override disconnectedCallback(): void {
        this._changeEventNames.forEach((name) => this.removeEventListener(name, this.setValidityValue));
        super.disconnectedCallback();
    }

    setCurrentItemState(isCurrentItem: boolean) {
        this._isCurrentItem = isCurrentItem;

        if (isCurrentItem) {
            this.setValidityValue();
        }
    }

    setValidityValue() {
        if (!this._isCurrentItem) {
            return;
        }

        const isValid = !this.querySelectorAll(':invalid').length;

        this.dispatchEvent(
            new CustomEvent('der.progressbar-item-validity', { detail: { valid: isValid }, bubbles: true })
        );
    }

    /**
     * Lit lifecycle hook – used here to notify parent progress bar when the label changes after
     * initial render (e.g. async translation load). The parent can't otherwise know to re-render
     * its list of step labels, since it only maps over current child properties.
     */
    override updated(changedProperties: Map<string | number | symbol, unknown>): void {
        if (changedProperties.has('label')) {
            this.dispatchEvent(
                new CustomEvent('der.progressbar-item-label-change', {
                    detail: { label: this.label },
                    bubbles: true,
                })
            );
        }
    }

    override render() {
        return html`<slot></slot>`;
    }
}
